/**
 * Custom API client for Kubb-generated code
 * This client is automatically used by all generated API functions
 * Configured for cookie-based authentication with Next.js proxy
 */

import type { RequestConfig, ResponseConfig } from "@kubb/plugin-client/clients/fetch";
import { flattenObject, type ParamsObject } from "../../utils/src/flattenObject";

// Mirrors TRIMBLE_SESSION_REJECTED_CODE in the API's trimble-session-rejected.exception.ts.
// The two runtimes never share a module, so the string is defined in both places.
export const TRIMBLE_SESSION_REJECTED_CODE = "trimble_session_rejected";

export type ClientAuthTokenResult = {
  token: string | null;
  // Why `token` is null: "unauthenticated" (never signed in, or explicitly logged out)
  // vs. "refresh_failed" (Trimble's own /oauth/token endpoint rejected or was
  // unreachable during a refresh). Used below to decide whether a 401 with no
  // Authorization header deserves the "reauth required" message or a clean login.
  // Returned alongside the token itself (not fetched separately, later) so a
  // concurrent, unrelated token fetch elsewhere in the app can't overwrite the reason
  // that actually applied to *this* request while its own fetch to our API was pending.
  failureReason: "unauthenticated" | "refresh_failed" | null;
};

// Token getter registered by the Trimble client so Kubb queries can inject
// the in-memory access token.
let _getToken: (() => Promise<ClientAuthTokenResult>) | null = null;

export function setClientAuthTokenGetter(fn: () => Promise<ClientAuthTokenResult>) {
  _getToken = fn;
}

export const SESSION_REVOKED_CODE = "session_revoked";
export const ACCOUNT_DISABLED_CODE = "account_disabled";
export const TERMINATED_SESSION_LOGIN_PATH = "/login?error=reauth_required";
export type SessionTerminationReason = typeof SESSION_REVOKED_CODE | typeof ACCOUNT_DISABLED_CODE | "unauthenticated";
export type SessionTermination = { reason: SessionTerminationReason; loginUrl: string };

let _onSessionTerminated: ((termination: SessionTermination) => void) | null = null;

export function setClientSessionTerminatedHandler(fn: ((termination: SessionTermination) => void) | null) {
  _onSessionTerminated = fn;
}

export function getSessionTerminationReason(status: number, code: string | undefined): SessionTerminationReason | null {
  if (status === 403) return code === ACCOUNT_DISABLED_CODE ? ACCOUNT_DISABLED_CODE : null;
  if (status !== 401) return null;
  if (code === SESSION_REVOKED_CODE) return SESSION_REVOKED_CODE;
  if (code === TRIMBLE_SESSION_REJECTED_CODE) return null;
  return "unauthenticated";
}

export function isAuthPagePath(pathname: string): boolean {
  return /\/(login|resolve)(\/|$)/.test(pathname) || /\/register(\/|$)/.test(pathname);
}

export function terminateClientSession(termination: SessionTermination): void {
  if (_onSessionTerminated) {
    _onSessionTerminated(termination);
  } else {
    window.location.href = termination.loginUrl;
  }
}

function unauthenticatedLoginUrl(
  currentPath: string,
  code: string | undefined,
  hadToken: boolean,
  tokenFailureReason: ClientAuthTokenResult["failureReason"] | undefined,
): string {
  const needsReauth = hadToken || tokenFailureReason === "refresh_failed";
  const isConfirmedExpiry = code === "token_expired";
  const errorParam = needsReauth ? (isConfirmedExpiry ? "session_expired" : "reauth_required") : null;
  return errorParam
    ? `/login?error=${errorParam}&returnTo=${encodeURIComponent(currentPath)}`
    : `/login?returnTo=${encodeURIComponent(currentPath)}`;
}

// Re-export types for the generated code to use
export type { RequestConfig };
export type ResponseErrorConfig<TError = unknown> = {
  response: {
    data: TError;
    status: number;
    statusText: string;
  };
};

// Circuit breaker to prevent hammering backend during outages.
// Server-side uses a no-op to avoid shared state across all SSR requests.
interface CircuitBreaker {
  isOpen(): boolean;
  recordSuccess(): void;
  recordServerError(): void;
}

const noopCircuitBreaker: CircuitBreaker = {
  isOpen: () => false,
  recordSuccess: () => {},
  recordServerError: () => {},
};

function createCircuitBreaker(threshold: number, cooldownMs: number): CircuitBreaker {
  let consecutiveFailures = 0;
  let openedAt = 0;

  return {
    isOpen() {
      if (consecutiveFailures < threshold) return false;
      if (Date.now() - openedAt >= cooldownMs) {
        consecutiveFailures = threshold - 1;
        return false;
      }
      return true;
    },
    recordSuccess() {
      consecutiveFailures = 0;
    },
    recordServerError() {
      consecutiveFailures++;
      if (consecutiveFailures >= threshold) {
        openedAt = Date.now();
      }
    },
  };
}

/**
 * Normalize a URL to a route template by replacing UUIDs and numeric IDs with `:id`.
 * This groups requests to the same endpoint under one circuit breaker.
 */
function normalizeRoute(url: string): string {
  const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
  const numericPattern = /\/(\d+)(\/|$)/g;
  return url.replace(uuidPattern, ":id").replace(numericPattern, "/:id$2");
}

const circuitBreakers = new Map<string, CircuitBreaker>();

function getCircuitBreaker(method: string, url: string): CircuitBreaker {
  if (typeof window === "undefined") return noopCircuitBreaker;
  // Keyed by method too, so failing reads (e.g. polling a processing file) can't block a write to the same URL
  const key = `${method.toUpperCase()} ${normalizeRoute(url)}`;
  let cb = circuitBreakers.get(key);
  if (!cb) {
    cb = createCircuitBreaker(5, 30_000);
    circuitBreakers.set(key, cb);
  }
  return cb;
}

/** @internal Exported for testing only. */
export const _normalizeRoute = normalizeRoute;

/** @internal Exported for testing only. */
export const _circuitBreakers = circuitBreakers;

// Get the API base URL
async function getApiBaseUrl(): Promise<string> {
  // On the server, we need to use the full URL for the proxy
  if (typeof window === "undefined") {
    // Get the base URL from environment or use a default
    const baseUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001";
    return `${baseUrl}/api/proxy`;
  }
  // On the client, use the relative path
  return "/api/proxy";
}

// Helper to forward the Authorization header from the incoming request (Trimble server-side)
async function getServerAuthorizationHeader(): Promise<string | null> {
  if (typeof window !== "undefined" || (typeof process !== "undefined" && process.env.NODE_ENV === "test")) {
    return null;
  }
  try {
    // @ts-ignore
    const nextHeaders = require("next/headers");
    const h = await nextHeaders.headers();
    return h.get("authorization") ?? null;
  } catch {
    return null;
  }
}

/**
 * Custom fetch client for cookie-based authentication
 * This matches Kubb's expected client signature
 */
async function client<TData, _TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>,
): Promise<ResponseConfig<TData>> {
  const baseUrl = await getApiBaseUrl();
  const { url, method = "GET", params, data, headers: configHeaders, ...rest } = config;
  const isFormData = typeof FormData !== "undefined" && data instanceof FormData;
  // Build the final URL with query parameters if needed
  let finalUrl: string = `${baseUrl}${url}` || "";
  if (params && typeof params === "object") {
    const searchParams = new URLSearchParams();

    // Use the flattenObject utility to handle nested params, skipping 'query' key for React Query
    flattenObject(params as ParamsObject, searchParams, { skipKeys: ["query"] });
    const queryString = searchParams.toString();
    if (queryString) {
      finalUrl = `${finalUrl}?${queryString}`;
    }
  }

  const headers: HeadersInit = {
    ...(!isFormData && { "Content-Type": "application/json" }),
    ...configHeaders,
  };

  // Captured once, per-request, from this specific _getToken() call — not re-read from
  // shared state later, so a concurrent request's own token fetch elsewhere in the app
  // can't overwrite the reason that applies to *this* request. See ClientAuthTokenResult.
  let tokenFailureReason: ClientAuthTokenResult["failureReason"] = null;

  if (typeof window !== "undefined") {
    // Client-side: inject the access token from the in-memory TID session
    const result = _getToken ? await _getToken() : null;
    if (result?.token) {
      (headers as Record<string, string>).authorization = `Bearer ${result.token}`;
    } else {
      tokenFailureReason = result?.failureReason ?? null;
    }
  } else {
    // Server-side: forward auth from the incoming request
    const authorization = await getServerAuthorizationHeader();
    if (authorization) {
      (headers as Record<string, string>).authorization = authorization;
    }
  }

  const cb = getCircuitBreaker(method, finalUrl);

  // Circuit breaker: reject immediately if open
  if (cb.isOpen()) {
    throw {
      response: {
        data: null,
        status: 503,
        statusText: "Service Unavailable (circuit breaker open)",
      },
    };
  }

  let response: Response;
  try {
    response = await fetch(finalUrl, {
      method,
      body: data ? (isFormData ? data : JSON.stringify(data)) : undefined,
      credentials: "include", // Important for cookie-based auth
      cache: "no-store",
      ...rest,
      headers,
    });
  } catch (error) {
    // React Query aborts in-flight requests whenever it refetches, which says nothing about backend health
    const isAbort = error instanceof DOMException && error.name === "AbortError";
    // Network-level failure (DNS, connection reset, etc.) — trip the breaker
    if (!isAbort) cb.recordServerError();
    throw error;
  }

  // Handle response based on Content-Type
  const contentType = response.headers.get("content-type") || "";
  let responseData: TData | null = null;

  if (contentType.includes("json")) {
    // Parse JSON responses
    responseData = await response.json().catch(() => null);
  } else if (
    contentType.includes("application/pdf") ||
    contentType.includes("application/octet-stream") ||
    contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
  ) {
    // Handle binary data (PDFs, Excel files, etc.)
    responseData = (await response.blob()) as TData;
  }

  if (!response.ok) {
    if (response.status >= 500) {
      cb.recordServerError();
    }

    if (typeof window !== "undefined" && !isAuthPagePath(window.location.pathname)) {
      const code = (responseData as { errors?: Array<{ code?: string }> } | null)?.errors?.[0]?.code;
      const reason = getSessionTerminationReason(response.status, code);
      if (reason) {
        const currentPath = window.location.pathname;
        const loginUrl =
          reason === "unauthenticated"
            ? unauthenticatedLoginUrl(
                currentPath,
                code,
                !!(headers as Record<string, string>).authorization,
                tokenFailureReason,
              )
            : TERMINATED_SESSION_LOGIN_PATH;
        terminateClientSession({ reason, loginUrl });
        return new Promise(() => {}); // Never resolves
      }
    }

    // Return error response in Kubb's expected format
    throw {
      response: {
        data: responseData,
        status: response.status,
        statusText: response.statusText,
      },
    };
  }

  cb.recordSuccess();

  // Return success response in Kubb's expected format
  return {
    data: responseData as TData,
  } as ResponseConfig<TData>;
}

// Export as default for Kubb to use
export default client;
